import { Dropdown } from 'src/components/dropdown';
import { CurrentBalanceCompany, DebtTypeEnum } from 'src/types/inputs';
import { inputIndex, inputType } from '../types';
import { Box, Typography, Button, IconButton } from '@mui/material';
import SpendInput from 'src/components/spend-input/spend-input-v2';
import CloseIcon from '@mui/icons-material/Close';
import { INSTITUTION_MAP, TYPE_OF_DEBT_MAP } from '../constants';

const MAX_DEBT = 99999;

type DebtProps = {
  direction?: 'row' | 'column';
  debt: inputType;
  unavailableInputs?: string[];
  onChange?: (
    debtIdx: string,
    inputIdx: inputIndex,
    value: string | number
  ) => void;
  onConfirm?: (idx: string) => void;
  onRemove?: (idx: string) => void;
};

const getUnavailableDebtTypes = (
  institution: CurrentBalanceCompany | '',
  unavailableInputs: string[]
) => {
  const unavailableDebtTypes = unavailableInputs.reduce((acc, item) => {
    const [debtType, inst] = item.split('-');
    if (institution && inst === institution && debtType) acc.push(debtType);
    return acc;
  }, [] as string[]);
  return unavailableDebtTypes;
};

const getUnavailableInstitutions = (
  debtType: DebtTypeEnum | '',
  unavailableInputs: string[]
) => {
  const unavailableInstitutions = unavailableInputs.reduce((acc, item) => {
    const [dt, institution] = item.split('-');
    if (debtType && dt === debtType && institution) acc.push(institution);
    return acc;
  }, [] as string[]);
  return unavailableInstitutions;
};

export const DebtInput = ({
  direction = 'row',
  debt,
  unavailableInputs = [],
  onChange,
  onConfirm,
  onRemove,
}: DebtProps) => {
  const handleOnChange = (inputIdx: inputIndex, value: string | number) => {
    if (onChange) onChange(debt.index, inputIdx, value);
  };

  const unavailableDebtTypes =
    getUnavailableDebtTypes(debt[inputIndex.INSTITUTION], unavailableInputs) ||
    [];
  const unavailableInstitutions =
    getUnavailableInstitutions(debt[inputIndex.DEBT_TYPE], unavailableInputs) ||
    [];

  const evaluateDisableConfirmation = () => {
    return (
      debt[inputIndex.AMOUNT] === null ||
      debt[inputIndex.AMOUNT] > MAX_DEBT ||
      debt[inputIndex.AMOUNT] <= 0 ||
      !debt[inputIndex.INSTITUTION] ||
      !debt[inputIndex.DEBT_TYPE]
    );
  };

  return (
    <Box display="flex" flexDirection={direction} gap={3} marginTop={{ md: 4 }}>
      <Box width={{ sm: '100%', md: '300px' }}>
        <Dropdown
          id="debt_type"
          defaultValue={debt[inputIndex.DEBT_TYPE]}
          label="Debt Type"
          options={TYPE_OF_DEBT_MAP}
          disabledOptions={unavailableDebtTypes}
          onChange={(value) => handleOnChange(inputIndex.DEBT_TYPE, value)}
          preferred={DebtTypeEnum.CreditCard}
          size="small"
        />
      </Box>
      <Box width={{ sm: '100%', md: '300px' }}>
        <Dropdown
          id="institution"
          defaultValue={debt[inputIndex.INSTITUTION] || ''}
          label="Institution"
          options={INSTITUTION_MAP}
          disabledOptions={unavailableInstitutions}
          onChange={(value) => handleOnChange(inputIndex.INSTITUTION, value)}
          size="small"
        />
      </Box>
      <SpendInput
        id={`spend-input-${debt.index}`}
        label="Amount"
        autoFocus={false}
        value={debt[inputIndex.AMOUNT]}
        isValid={
          debt[inputIndex.AMOUNT] === null || debt[inputIndex.AMOUNT] > 0
        }
        helperText="Choose an amount greater than $0"
        onChange={(value) => handleOnChange(inputIndex.AMOUNT, value)}
        sx={{
          '& .MuiInputBase-root': {
            '& fieldset': { borderColor: `neutral.lighter` },
          },
          minWidth: '120px',
        }}
      />
      {onConfirm && (
        <Button
          sx={{ minWidth: '136px', width: '136px', height: '44px' }}
          onClick={() => onConfirm(debt.index)}
          disabled={evaluateDisableConfirmation()}
        >
          <Typography sx={{ textTransform: 'uppercase' }}>confirm</Typography>
        </Button>
      )}
      {onRemove && (
        <IconButton
          onClick={() => onRemove(debt.index)}
          aria-label="Remove this debt"
          sx={{
            color: 'text.primary',
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
};
